import React, { useState, useEffect, useRef, useCallback} from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import * as THREE from 'three';
import './App.css';

const useModelCache = () => {
  const [modelCache, setModelCache] = useState({});
  const [loadingModels, setLoadingModels] = useState({});

  const loadModel = useCallback((modelPath) => {
    return new Promise((resolve, reject) => {
      if (modelCache[modelPath]) {
        resolve(modelCache[modelPath]);
        return;
      }

      if (loadingModels[modelPath]) {
        loadingModels[modelPath].then(resolve);
        return;
      }

      const loader = new OBJLoader();
      const modelLoading = new Promise((innerResolve, innerReject) => {
        loader.load(
          modelPath,
          (obj) => {
            const newModelRef = new THREE.Group();

            const box = new THREE.Box3().setFromObject(obj);
            const size = box.getSize(new THREE.Vector3());
            const maxDimension = Math.max(size.x, size.y, size.z);
            obj.rotation.x = -Math.PI / 2;
            const scaleFactor = 2.5 / maxDimension;
            obj.scale.set(scaleFactor, scaleFactor, scaleFactor);

            box.setFromObject(obj);
            const center = box.getCenter(new THREE.Vector3());
            obj.position.sub(center);

            obj.traverse((child) => {
              if (child.isMesh) {
                const edges = new THREE.EdgesGeometry(child.geometry, 45);
                const baseOpacity = 0.4 + Math.random() * 0.35;
                const whiteness = 0.85 + Math.random() * 0.15;
                const lineColor = new THREE.Color(whiteness, whiteness, 1);

                const lineMaterial = new THREE.LineBasicMaterial({
                  color: lineColor,
                  opacity: baseOpacity,
                  transparent: true,
                });
                lineMaterial.userData.baseOpacity = baseOpacity;

                const edgeLines = new THREE.LineSegments(edges, lineMaterial);
                edgeLines.position.copy(child.position);
                edgeLines.rotation.copy(child.rotation);
                edgeLines.scale.copy(child.scale);
                child.parent.add(edgeLines);
                child.parent.remove(child);
              }
            });

            newModelRef.add(obj);

            setModelCache(prev => ({ ...prev, [modelPath]: newModelRef }));
            setLoadingModels(prev => {
              const updated = { ...prev };
              delete updated[modelPath];
              return updated;
            });

            innerResolve(newModelRef);
            resolve(newModelRef);
          },
          undefined,
          (error) => {
            innerReject(error);
            reject(error);
          }
        );
      });

      setLoadingModels(prev => ({ ...prev, [modelPath]: modelLoading }));
      return modelLoading;
    });
  }, [modelCache, loadingModels]);

  return { modelCache, loadModel };
};

const ModelLoader = ({ modelPath }) => {
  const [model, setModel] = useState(null);
  const { loadModel } = useModelCache();
  const groupRef = useRef(null);
  const clockRef = useRef(0);

  useEffect(() => {
    loadModel(modelPath).then(setModel).catch(console.error);
  }, [modelPath, loadModel]);

  useFrame((_, delta) => {
    if (groupRef.current) groupRef.current.rotation.y += 0.003;
    if (model) {
      clockRef.current += delta;
      const pulse = Math.sin(clockRef.current * 2.5) * 1.5 + 1.5;
      model.traverse((child) => {
        if (child.isLineSegments && child.material) {
          const base = child.material.userData.baseOpacity ?? 0.5;
          child.material.opacity = Math.min(1, Math.max(0.08, base * 0.4 + pulse * 0.75));
        }
      });
    }
  });

  return (
    <group ref={groupRef}>
      {!model && (
        <mesh>
          <boxGeometry args={[1, 1, 1]} />
          <meshBasicMaterial color="white" wireframe={true} />
        </mesh>
      )}
      {model && <primitive object={model} />}
    </group>
  );
};

const App = () => {
  const [models, setModels] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [highlightedIndex, setHighlightedIndex] = useState(null);
  const [asciiArt, setAsciiArt] = useState('');
  const [asciiIndex, setAsciiIndex] = useState(0);
  const [asciiComplete, setAsciiComplete] = useState(false);
  const { loadModel } = useModelCache();
  const isIdiotPath = window.location.pathname.toLowerCase().replace(/\/$/, '').endsWith('/idiot');
  const [isIdiotMode, setIsIdiotMode] = useState(isIdiotPath || window.innerWidth < 1150);

  // --- beep sound effect setup ---
  const audioCtxRef = useRef(null);
  const isFirstSelect = useRef(true);

  const playBeep = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(91.13, ctx.currentTime);

      gainNode.gain.setValueAtTime(0.001, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.16);
    } catch (e) {
      console.error('Beep failed:', e);
    }
  }, []);

  useEffect(() => {
    if (isFirstSelect.current) {
      isFirstSelect.current = false;
      return;
    }
    playBeep();
  }, [selectedIndex, playBeep]);
  // --- end beep sound effect setup ---

  const selectedModelData = models[selectedIndex];

  const navigateToSelected = useCallback(() => {
    if (selectedModelData?.site) {
      window.location.href = selectedModelData.site;
    }
  }, [selectedModelData]);

  const handleCanvasClick = () => navigateToSelected();

  useEffect(() => {
    const handleResize = () => setIsIdiotMode(isIdiotPath || window.innerWidth < 1150);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isIdiotPath]);

  useEffect(() => {
    fetch('./models.json')
      .then((response) => response.json())
      .then((data) => {
        setModels(data);
        setSelectedIndex(0);
        data.forEach(model => loadModel(`./stls/${model.file}`));
      })
      .catch((error) => console.error('Error loading models:', error));
  }, [loadModel]);

  const asciiArtString = `
......................................................................................................
..............@@@@......................@@@....................@@@@...................................
.............@@..@@...................@@@@@@@.................@@@@@@@..........@@@@@@@@@@@@@@@@@@@@...
............@@....@@.................@@@@@@@@@..............@@@@@@@@@@.........@@@@@@@@@@@@@@@@@@@....
............@......@...............@@@@@@@@@@@@@...........@@@@@@@@@@@@........@@@@@@@@@@@@@@@@@......
...........@@......@@.............@@@@@@@@@@@@@@@.........@@@@@@@@@@@@@@@......@@@@@@@@@@@@@@@@.......
..........@@........@@.............@@@@@@@@@@@@@..........@@@@@@@@@@@@@@.................@@@@.........
.........@@..........@@.............@@@@@@@@@@@.............@@@@@@@@@@@..................@@@..........
........@@...........@@..............@@@@@@@@................@@@@@@@@....................@@...........
.......@@.............@@.............@@@@@@@@................@@@@@@@@....................@............
.......@@.....@@@@.....@@...........@@@.@@..@@..............@@@.@@.@@@.........@@@@@..................
......@@......@..@......@@.........@@@......@@@...........@@@@......@@@........@@@@@..................
.....@@......@....@@@@@@@@........@@@...@@@..@@@@.........@@@..@@@...@@@........@@@@@.................
....@@......@.@@@@@@@@@@@@@......@@@...@@@@@..@@@........@@@...@@@@...@@@.......@@@@@@@...............
....@......@@.@@@.........@@....@@@....@@@@@...@@@......@@@...@@@@@@...@@@.......@@@@@@@@@@@@@@@@.....
..@@@.....@@....@@@........@@..@@@@@@@@@@@@@@@@@@@@....@@@@@@@@@@@@@@@@@@@@........@@@@@@@@@@@@@@.....
..@@@@@@@@@........@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@..@@@@@@@@@@@@@@@@@@@@@@.........@@@@@@@@@@@@.....
......................................................................................................
  `;

  useEffect(() => {
    if (asciiIndex < asciiArtString.length) {
      const interval = setInterval(() => {
        setAsciiArt((prev) => prev + asciiArtString[asciiIndex]);
        setAsciiIndex((prev) => prev + 1);
      }, 5);
      return () => clearInterval(interval);
    } else {
      setAsciiComplete(true);
    }
  }, [asciiIndex, asciiArtString]);

  const handleKeyPress = useCallback((event) => {
    if (models.length === 0) return;
    if (event.key === 'ArrowDown') {
      setSelectedIndex((prev) => (prev + 1) % models.length);
    } else if (event.key === 'ArrowUp') {
      setSelectedIndex((prev) => (prev - 1 + models.length) % models.length);
    } else if (event.key === 'Enter') {
      navigateToSelected();
    }
  }, [models, navigateToSelected]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleKeyPress]);

  if (isIdiotMode) {
    return (
      <div style={{ minHeight: '100vh', height: 'auto', overflow: 'visible', padding: '20px', boxSizing: 'border-box' }}>
        <h1>aii3.org</h1>
        <p>pixel width too low for advanced 3d site rendering (less then 1150px)</p>
        <p>simplified rendering displayed</p>
        <p>PROJECTS:</p>
        {models.map((model, index) => (
          <div key={`${model.file}-${index}`}>
            <a href={model.site}>{model.name}</a>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="container">
      <div className="sidebar">
        <h3>
          <h4>Select a Project</h4>
           — then click on the floating model
        </h3>

        <ul>
          {models.map((model, index) => (
            <li
              key={`${model.file}-${index}`}
              className={`${selectedIndex === index ? 'selected' : ''} ${highlightedIndex === index ? 'highlighted' : ''}`}
              onClick={() => setSelectedIndex(index)}
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseLeave={() => setHighlightedIndex(null)}
            >
              {selectedIndex === index ? `> ${model.name}` : model.name}
            </li>
          ))}
        </ul>
      </div>

      <div className="right-container">
          {!asciiComplete && (
            <div className="ascii-art">{asciiArt}</div>
          )}
          {asciiComplete && selectedModelData && (
            <h3
              style={{
                position: 'absolute',
                top: '24px',
                left: 0,
                right: 0,
                margin: 0,
                zIndex: 1,
                pointerEvents: 'none',
                textAlign: 'center',
                color: '#fff',
                fontFamily: "'Courier New', Courier, monospace",
                fontWeight: 'normal',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontSize: '64px',
              }}
            >
              {selectedModelData.name}
            </h3>
          )}
          {asciiComplete && (
            <Canvas
              style={{ backgroundColor: 'black', cursor: 'pointer' }}
              camera={{ position: [2.5, 2.5, 2.5], fov: 45 }}
              onClick={handleCanvasClick}
            >
              <ambientLight intensity={0.5} />
              <spotLight position={[10, 10, 10]} intensity={1} />
              <directionalLight position={[5, 5, 5]} intensity={1} />
              {selectedModelData && <ModelLoader modelPath={`./stls/${selectedModelData.file}`} />}
              <OrbitControls
                enableZoom={false}
                minPolarAngle={Math.PI / 4}
                maxPolarAngle={Math.PI / 4}
              />
            </Canvas>
          )}
      </div>
    </div>
  );
};

export default App;